import type { ChangeEvent, CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/components/generals/count-up'
import { TweenNumber } from '@/components/generals/tween-number'

import {
    AREA_MAX,
    AREA_MIN,
    AREA_STEP,
    STRUCTURE_OPTIONS
} from '@/constants/materials'

const BASELINE = STRUCTURE_OPTIONS[0]

/**
 * Floor area and structure in, embodied carbon out: the total in tonnes and the saving against an
 * all-concrete building, gliding to each new value as the slider moves.
 */
export const CarbonCalculator: FC = (): ReactNode => {
    const [area, setArea] = useState(6000)
    const [structure, setStructure] = useState(STRUCTURE_OPTIONS[3].id)
    const option = STRUCTURE_OPTIONS.find(item => item.id === structure) ?? STRUCTURE_OPTIONS[3]
    const total = (area * option.perM2) / 1000
    const baseline = (area * BASELINE.perM2) / 1000
    const saving = baseline - total
    const savingPercent = baseline > 0 ? (saving / baseline) * 100 : 0
    const fill = ((area - AREA_MIN) / (AREA_MAX - AREA_MIN)) * 100

    return (
        <div className='grid gap-px border-y border-hairline bg-hairline desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
            <div className='bg-background px-gutter py-10 desk:py-14'>
                <div className='flex items-baseline justify-between gap-6'>
                    <label htmlFor='calculator-area' className='hud text-mute'>
                        Floor area
                    </label>

                    <output htmlFor='calculator-area' className='font-heading text-[32px] font-medium leading-none tracking-[-0.03em] text-ink'>
                        {formatNumber(area, 0)}
                        <span className='hud ml-2 text-mute'>
                            m²
                        </span>
                    </output>
                </div>

                <input
                    id='calculator-area'
                    type='range'
                    min={AREA_MIN}
                    max={AREA_MAX}
                    step={AREA_STEP}
                    value={area}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setArea(Number(event.target.value))}
                    aria-valuetext={`${formatNumber(area, 0)} square metres`}
                    className='hud-range mt-6 w-full'
                    style={{ '--fill': `${fill}%` } as CSSProperties}
                />

                <p className='hud mt-3 flex justify-between text-mute' aria-hidden='true'>
                    <span>
                        {formatNumber(AREA_MIN, 0)} m²
                    </span>

                    <span>
                        {formatNumber(AREA_MAX, 0)} m²
                    </span>
                </p>

                <fieldset className='mt-10'>
                    <legend className='hud text-mute'>
                        Structure
                    </legend>

                    <div className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 desk:grid-cols-2 xl:grid-cols-4'>
                        {STRUCTURE_OPTIONS.map(item => (
                            <label key={item.id} className='relative cursor-pointer'>
                                <input
                                    type='radio'
                                    name='calculator-structure'
                                    value={item.id}
                                    checked={structure === item.id}
                                    onChange={() => setStructure(item.id)}
                                    className='peer sr-only'
                                />

                                <span className={cn(
                                    'flex h-full flex-col gap-1 border px-3 py-3 transition-colors duration-300 peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-signal',
                                    structure === item.id ? 'border-ink bg-ink text-background' : 'border-hairline text-ink hover:border-mute'
                                )}>
                                    <span className='hud'>
                                        {item.label}
                                    </span>

                                    <span className={cn('hud', structure === item.id ? 'text-background/70' : 'text-mute')}>
                                        {item.perM2} kg/m²
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                </fieldset>
            </div>

            <div className='bg-background px-gutter py-10 desk:py-14'>
                <p className='sr-only' aria-live='polite'>
                    {`${option.label}, ${formatNumber(area, 0)} square metres: ${formatNumber(total, 0)} tonnes of CO2e, ${formatNumber(saving, 0)} tonnes less than all concrete.`}
                </p>

                <p className='hud text-mute'>
                    Embodied carbon / {option.label}
                </p>

                <p className='mt-4 font-heading text-[clamp(64px,8vw,120px)] font-medium leading-[0.9] tracking-[-0.045em] text-ink'>
                    <TweenNumber value={total} duration={600} />
                </p>

                <p className='hud mt-3 text-mute'>
                    Tonnes CO2e / A1 to A3
                </p>

                <div className='mt-10 space-y-4' aria-hidden='true'>
                    <div>
                        <p className='hud flex justify-between text-mute'>
                            <span>
                                {option.label}
                            </span>

                            <span className='text-ink'>
                                {formatNumber(total, 0)} t
                            </span>
                        </p>

                        <div className='mt-2 h-2 bg-hairline/60'>
                            <span className='race-bar block h-full bg-ink' style={{ width: `${(option.perM2 / BASELINE.perM2) * 100}%` }} />
                        </div>
                    </div>

                    <div>
                        <p className='hud flex justify-between text-mute'>
                            <span>
                                {BASELINE.label}
                            </span>

                            <span className='text-ink'>
                                {formatNumber(baseline, 0)} t
                            </span>
                        </p>

                        <div className='mt-2 h-2 bg-hairline/60'>
                            <span className='block h-full w-full bg-mute/60' />
                        </div>
                    </div>
                </div>

                <dl className='mt-10 grid grid-cols-2 border-t border-hairline'>
                    <div className='border-r border-hairline py-4 pr-4'>
                        <dt className='hud text-mute'>
                            Saving vs all concrete
                        </dt>

                        <dd className='mt-2 font-heading text-[30px] font-medium leading-none tracking-[-0.03em] text-signal'>
                            <TweenNumber value={saving} duration={600} suffix=' t' />
                        </dd>
                    </div>

                    <div className='py-4 pl-4'>
                        <dt className='hud text-mute'>
                            Reduction
                        </dt>

                        <dd className='mt-2 font-heading text-[30px] font-medium leading-none tracking-[-0.03em] text-ink'>
                            <TweenNumber value={savingPercent} duration={600} suffix='%' />
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
    )
}
